/**
 * Утилитарный класс для безопасного форматирования полей документов
 */
export class DocumentFieldFormatter {
  /**
   * Безопасно преобразует значение в строку, возвращая пустую строку для null/undefined
   * @param value - значение для преобразования
   * @param defaultValue - значение по умолчанию (по умолчанию пустая строка)
   * @returns строковое представление значения или значение по умолчанию
   */
  static safeValue(value: string | number | null | undefined, defaultValue: string = ''): string {
    if (value === null || value === undefined || value === '') {
      return defaultValue;
    }
    return String(value);
  }

  /**
   * Безопасно конкатенирует строки, пропуская пустые значения
   * @param parts - массив частей для конкатенации
   * @param separator - разделитель (по умолчанию пробел)
   * @returns объединенная строка
   */
  static safeConcatenate(parts: { value: string | number | null | undefined; prefix?: string; suffix?: string }[], separator: string = ' '): string {
    const validParts = parts
      .filter(part => part.value !== null && part.value !== undefined && part.value !== '')
      .map(part => {
        const prefix = part.prefix || '';
        const suffix = part.suffix || '';
        return `${prefix}${part.value}${suffix}`;
      });

    return validParts.join(separator);
  }

  /**
   * Год урожая для документа: заданный год или null. Год необязателен (решение 2026-11-04); 0 — пустой год
   * из старых записей сервера, его тоже не печатаем.
   */
  static cropYear(value: number | string | null | undefined): number | null {
    // Заявка из резервной копии (JSON.parse без приведения типов) может принести год строкой — понимаем и её,
    // чтобы карточка и документы не расходились.
    const year = typeof value === 'string' ? Number(value.trim()) : value;
    return typeof year === 'number' && Number.isInteger(year) && year > 0 ? year : null;
  }

  /** Наименование груза в ТТН (ячейка F42): «Пшеница 4 класс, год урожая 2025» или без года. */
  static ttnFreight(freightName: string | null | undefined, cropYear: number | string | null | undefined): string {
    return DocumentFieldFormatter.safeConcatenate(
      [{ value: freightName }, { value: DocumentFieldFormatter.cropYear(cropYear), prefix: 'год урожая ' }],
      ', '
    );
  }

  /** Груз в ТрН (ячейка B25): «Пшеница 4 класс, 2025г.» или без года. */
  static trnFreight(freightName: string | null | undefined, cropYear: number | string | null | undefined): string {
    return DocumentFieldFormatter.safeConcatenate(
      [{ value: freightName }, { value: DocumentFieldFormatter.cropYear(cropYear), suffix: 'г.' }],
      ', '
    );
  }

  /**
   * Безопасно форматирует шаблонную строку, заменяя плейсхолдеры
   * @param template - шаблон с плейсхолдерами вида ${key}
   * @param values - объект со значениями для замены
   * @param defaultValue - значение по умолчанию для пустых полей
   * @returns отформатированная строка
   */
  static safeTemplate(template: string, values: Record<string, string | number | null | undefined>, defaultValue: string = ''): string {
    return template.replace(/\$\{(\w+)\}/g, (match, key) => this.safeValue(values[key], defaultValue));
  }
}
